import { useMemo } from 'react'
import useSWR from 'swr'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import AppSelect from '@/components/zthd/app-select'
import { DropdownCountry1 } from '@/components/DropdownCountry'
import Loader from '@/components/zthd/Loader'
import ModelCurve from './components/model-curve-card'
import FundPool from './components/fund-pool-card'
import CompoundExpansion from './components/compound-expansion-card'
import FixedExpansion from './components/fixed-expansion-card'
import ModelVsActual from './components/model-vs-actual-card'
import DailyMetrics from './components/daily-metrics-card'
import RecoveryDatePicker from './components/recovery-date-picker'
import KeyMetrics from './components/key-metrics-cards'
import { useRecoveryStore } from '@/stores/recovery-store'

const RecoveryModel = () => {
  const { pid, country, startDate, endDate, setPid, setCountry } =
    useRecoveryStore()

  // Build the API id: total_${country}_${pid}
  const id = useMemo(() => `total_${country}_${pid}`, [country, pid])

  // Fetch cumulative ROAS data
  const { data, isLoading, error } = useSWR([
    `/troas/${id}/cumulative`,
    { startDate, endDate },
  ])

  // Transform roas data to chart-friendly format
  const roasData = useMemo(() => {
    if (!data?.data?.roas) return []

    const roas = data.data.roas
    const result = []

    // Sort by day number
    const sortedKeys = Object.keys(roas)
      .filter((key) => key.startsWith('day'))
      .sort((a, b) => {
        const dayA = parseInt(a.replace('day', ''))
        const dayB = parseInt(b.replace('day', ''))
        return dayA - dayB
      })

    sortedKeys.forEach((key) => {
      const dayNum = parseInt(key.replace('day', ''))
      result.push({
        day: dayNum,
        dayLabel: `Day ${dayNum}`,
        roas: roas[key].roas,
        costsSum: roas[key].costsSum,
        revenuesSum: roas[key].revenuesSum,
        dayCount: roas[key].day_count,
        startDate: roas[key].start_date,
        endDate: roas[key].end_date,
      })
    })

    return result
  }, [data])

  // Calculate daily recovery rate from cumulative data
  const dailyRates = useMemo(() => {
    if (roasData.length < 2) return []

    const rates = []
    for (let i = 0; i < roasData.length; i++) {
      const current = roasData[i]
      const prev = i > 0 ? roasData[i - 1] : null

      // Daily rate = (current roas - previous roas) for the day's incremental recovery
      const dailyRate = prev ? current.roas - prev.roas : current.roas
      rates.push({
        day: current.day,
        dailyRate: Math.max(0, dailyRate),
        cumulativeRate: current.roas,
      })
    }

    return rates
  }, [roasData])

  if (isLoading) {
    return <Loader>Loading recovery model data...</Loader>
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-600">
        Error loading data: {error.message}
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-4">
        <AppSelect value={pid} onValueChange={setPid} />
        <DropdownCountry1 value={country} update={setCountry} />
        <RecoveryDatePicker />
      </div>

      {/* Key Metrics Cards - 当前模型的基础数据 */}
      <KeyMetrics roasData={roasData} />

      <Tabs defaultValue="model" className="w-full">
        <TabsList className="grid w-[900px] grid-cols-6 gap-2">
          <TabsTrigger value="model">Model Curve</TabsTrigger>
          <TabsTrigger value="daily-metrics">Daily Metrics</TabsTrigger>
          <TabsTrigger value="compare">Model vs Actual</TabsTrigger>
          <TabsTrigger value="fund-pool">Fund Pool</TabsTrigger>
          <TabsTrigger value="compound">Compound</TabsTrigger>
          <TabsTrigger value="fixed">Fixed</TabsTrigger>
        </TabsList>

        <TabsContent value="model" className="w-full">
          <ModelCurve roasData={roasData} dailyRates={dailyRates} />
        </TabsContent>

        <TabsContent value="daily-metrics" className="w-full">
          <DailyMetrics roasData={roasData} />
        </TabsContent>

        <TabsContent value="compare" className="w-full">
          <ModelVsActual roasData={roasData} dailyRates={dailyRates} />
        </TabsContent>

        <TabsContent value="fund-pool" className="w-full">
          <FundPool roasData={roasData} dailyRates={dailyRates} />
        </TabsContent>

        <TabsContent value="compound" className="w-full">
          <CompoundExpansion roasData={roasData} dailyRates={dailyRates} />
        </TabsContent>

        <TabsContent value="fixed" className="w-full">
          <FixedExpansion roasData={roasData} dailyRates={dailyRates} />
        </TabsContent>
      </Tabs>
    </div>
  )
}

export default RecoveryModel
